import { useEffect, useRef, useState } from "react";
import { Check, Phone, Home } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { trackFormStart, trackFormStep, trackFormSubmit } from "@/lib/conversionTracking";
import AuMobileInput from "@/components/AuMobileInput";
import { AU_MOBILE_ERROR, isValidAuMobile, toE164AuMobile } from "@/lib/auPhone";

interface FormData {
  name: string;
  phone: string;
  email: string;
  move_size: string;
  move_date: string;
  date_flexible: boolean;
  pickup_suburb: string;
  dropoff_suburb: string;
  access: string;
}

const initialForm: FormData = {
  name: "",
  phone: "",
  email: "",
  move_size: "",
  move_date: "",
  date_flexible: false,
  pickup_suburb: "",
  dropoff_suburb: "",
  access: "",
};

const MOVE_SIZES = [
  "Few Items",
  "1 Bedroom",
  "2 Bedrooms",
  "3 Bedrooms",
  "4+ Bedrooms",
  "Office Move",
  "Single Heavy Item",
  "Not Sure",
];

const ACCESS_OPTIONS = ["No Stairs", "Stairs", "Lift", "Not Sure"];

const TRUST = ["Fully Insured", "Melbourne Based", "No Hidden Costs", "Fast Response"];

const TOTAL_STEPS = 4;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Focus ring shared by every interactive element in the form. */
const FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#FFC300]/45 focus-visible:border-[#FFC300]";

const FloatingField = ({
  id,
  label,
  value,
  onChange,
  onBlur,
  error,
  valid,
  type = "text",
  inputMode,
  autoComplete,
  inputRef,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string;
  valid?: boolean;
  type?: string;
  inputMode?: "text" | "email" | "numeric";
  autoComplete?: string;
  inputRef?: React.Ref<HTMLInputElement>;
}) => {
  const errorId = `${id}-error`;
  return (
    <div>
      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          type={type}
          inputMode={inputMode}
          autoComplete={autoComplete}
          placeholder=" "
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          className={`peer w-full px-4 pt-6 pb-2 min-h-[60px] border-2 rounded-xl bg-white text-[#1a1a1a] font-medium focus:outline-none transition-all duration-200 ease-out focus:border-[#FFC300] focus:shadow-[0_0_0_4px_rgba(255,195,0,0.22)] ${
            valid ? "pr-11" : ""
          } ${
            error
              ? "border-red-500 focus:border-red-500 focus:shadow-[0_0_0_4px_rgba(239,68,68,0.18)]"
              : valid
                ? "border-[#4caf50]/60"
                : "border-[#1a1a1a]/10"
          }`}
        />
        <label
          htmlFor={id}
          className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 origin-left text-base font-medium text-[#1a1a1a]/50 transition-all duration-200 ease-out motion-reduce:transition-none peer-focus:top-[0.4rem] peer-focus:translate-y-0 peer-focus:text-xs peer-focus:font-semibold peer-focus:text-[#1a1a1a]/55 peer-[:not(:placeholder-shown)]:top-[0.4rem] peer-[:not(:placeholder-shown)]:translate-y-0 peer-[:not(:placeholder-shown)]:text-xs peer-[:not(:placeholder-shown)]:font-semibold peer-[:not(:placeholder-shown)]:text-[#1a1a1a]/55"
        >
          {label}
        </label>
        {valid && !error && (
          <Check
            aria-hidden="true"
            strokeWidth={3}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#2e7d32] animate-in fade-in zoom-in-75 duration-200 motion-reduce:animate-none"
          />
        )}
      </div>
      {error && (
        <p id={errorId} className="text-red-500 text-xs mt-1.5 font-semibold break-words">
          {error}
        </p>
      )}
    </div>
  );
};

const SelectCard = ({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    className={`relative flex items-center justify-between gap-3 w-full min-h-[64px] px-5 py-4 rounded-xl border-2 text-left font-bold text-base transition-all duration-200 active:scale-[0.98] motion-reduce:active:scale-100 ${FOCUS_RING} ${
      selected
        ? "border-[#FFC300] bg-[#FFC300]/15 text-[#1a1a1a] shadow-[0_8px_24px_rgba(255,195,0,0.25)]"
        : "border-[#1a1a1a]/10 bg-white text-[#1a1a1a] hover:border-[#1a1a1a]/30 hover:shadow-[0_6px_18px_rgba(0,0,0,0.06)]"
    }`}
  >
    <span>{label}</span>
    <span
      className={`flex items-center justify-center w-6 h-6 rounded-full shrink-0 transition-all duration-200 motion-reduce:transition-none ${
        selected ? "bg-[#1a1a1a] scale-100 opacity-100" : "scale-50 opacity-0"
      }`}
    >
      <Check className="w-4 h-4 text-[#FFC300]" strokeWidth={3} />
    </span>
  </button>
);

const QuoteForm = () => {
  const [step, setStep] = useState(1);
  const [dir, setDir] = useState<"fwd" | "back">("fwd");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<FormData>(initialForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof FormData, boolean>>>({});
  const firstFieldRef = useRef<HTMLInputElement | null>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!submitted) firstFieldRef.current?.focus({ preventScroll: true });
  }, [step, submitted]);

  const setField = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    if (!startedRef.current) {
      startedRef.current = true;
      trackFormStart("quote_wizard");
    }
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const markTouched = (field: keyof FormData) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  /** Pure per step validity, used for the progress ticks. */
  const isStepComplete = (n: number): boolean => {
    if (n === 1)
      return (
        Boolean(form.name.trim()) &&
        isValidAuMobile(form.phone) &&
        EMAIL_RE.test(form.email.trim())
      );
    if (n === 2) return Boolean(form.move_size);
    if (n === 3) return form.date_flexible || Boolean(form.move_date);
    if (n === 4)
      return (
        Boolean(form.pickup_suburb.trim()) &&
        Boolean(form.dropoff_suburb.trim()) &&
        Boolean(form.access)
      );
    return false;
  };

  const fieldValid = (field: keyof FormData): boolean => {
    if (!touched[field] || errors[field]) return false;
    if (field === "phone") return isValidAuMobile(form.phone);
    if (field === "email") return EMAIL_RE.test(form.email.trim());
    return Boolean(String(form[field]).trim());
  };

  const validateStep = (current: number): boolean => {
    const e: Partial<Record<keyof FormData, string>> = {};
    if (current === 1) {
      if (!form.name.trim()) e.name = "Name is required";
      if (!form.phone.trim()) e.phone = "Phone number is required";
      else if (!isValidAuMobile(form.phone)) e.phone = AU_MOBILE_ERROR;
      if (!form.email.trim()) e.email = "Email is required";
      else if (!EMAIL_RE.test(form.email.trim()))
        e.email = "Enter a valid email address";
    }
    if (current === 2 && !form.move_size) e.move_size = "Please choose your move size";
    if (current === 3 && !form.date_flexible && !form.move_date)
      e.move_date = "Choose a date or select flexible";
    if (current === 4) {
      if (!form.pickup_suburb.trim()) e.pickup_suburb = "Pickup suburb is required";
      if (!form.dropoff_suburb.trim()) e.dropoff_suburb = "Drop off suburb is required";
      if (!form.access) e.access = "Please choose the access type";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (!validateStep(step)) {
      firstFieldRef.current?.focus({ preventScroll: true });
      return;
    }
    trackFormStep("quote_wizard", step, TOTAL_STEPS);
    setDir("fwd");
    setStep((s) => Math.min(TOTAL_STEPS, s + 1));
  };

  const back = () => {
    setErrors({});
    setDir("back");
    setStep((s) => Math.max(1, s - 1));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (step < TOTAL_STEPS) {
      next();
      return;
    }
    if (!validateStep(TOTAL_STEPS)) return;

    setLoading(true);
    const submittedAt = new Date();
    const quoteRequestId = crypto.randomUUID();
    const phoneE164 = toE164AuMobile(form.phone);
    const details = [
      `Move size: ${form.move_size}`,
      `Access: ${form.access}`,
      form.date_flexible ? "Moving date: Flexible" : null,
    ]
      .filter(Boolean)
      .join(" • ");

    const { error } = await supabase.from("quote_requests").insert({
      id: quoteRequestId,
      name: form.name.trim(),
      phone: phoneE164,
      email: form.email.trim(),
      pickup_suburb: form.pickup_suburb.trim(),
      dropoff_suburb: form.dropoff_suburb.trim(),
      move_date: form.date_flexible ? null : form.move_date,
      details,
    });

    if (error) {
      setLoading(false);
      toast.error("Something went wrong. Please try again or call us directly.");
      return;
    }

    try {
      await supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "new-quote-request",
          quoteRequestId,
          idempotencyKey: `new-quote-${phoneE164}-${submittedAt.getTime()}`,
        },
      });
    } catch {
      // Never block the customer confirmation on the notification email
    }

    try {
      await supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "quote-confirmation",
          quoteRequestId,
          idempotencyKey: `quote-confirmation-${form.email.trim()}-${submittedAt.getTime()}`,
        },
      });
    } catch {
      // Never block the success state on the confirmation email
    }

    setLoading(false);
    trackFormStep("quote_wizard", TOTAL_STEPS, TOTAL_STEPS);
    void trackFormSubmit(
      "quote_wizard",
      {
        move_size: form.move_size,
        access: form.access,
        date_flexible: form.date_flexible,
        pickup_suburb: form.pickup_suburb.trim(),
        dropoff_suburb: form.dropoff_suburb.trim(),
      },
      { email: form.email, phone: phoneE164 },
    );
    setSubmitted(true);
  };

  const inputClass = (field: keyof FormData) =>
    `w-full px-4 py-4 min-h-[60px] border-2 rounded-xl bg-white text-[#1a1a1a] font-medium placeholder:text-[#1a1a1a]/40 focus:border-[#FFC300] focus:outline-none focus:shadow-[0_0_0_4px_rgba(255,195,0,0.22)] transition-all duration-200 ease-out ${
      errors[field]
        ? "border-red-500 focus:border-red-500 focus:shadow-[0_0_0_4px_rgba(239,68,68,0.18)]"
        : fieldValid(field)
          ? "border-[#4caf50]/60"
          : "border-[#1a1a1a]/10"
    }`;

  const primaryBtn =
    `w-full bg-[#1a1a1a] text-[#FFC300] py-4 text-lg font-bold rounded-xl transition-all duration-200 ease-out shadow-[0_6px_16px_rgba(0,0,0,0.12)] hover:opacity-95 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(0,0,0,0.18)] active:translate-y-0 active:scale-[0.99] active:shadow-[0_4px_12px_rgba(0,0,0,0.14)] motion-reduce:transition-none motion-reduce:hover:translate-y-0 motion-reduce:active:scale-100 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-[0_6px_16px_rgba(0,0,0,0.12)] ${FOCUS_RING}`;

  const errorText = (field: keyof FormData) =>
    errors[field] ? (
      <p id={`${field}-error`} className="text-red-500 text-xs mt-1.5 font-semibold break-words">
        {errors[field]}
      </p>
    ) : null;

  return (
    <section id="quote" className="scroll-mt-28 bg-[#f7f7f7] py-16 md:py-20">
      <div className="container max-w-2xl">
        {submitted ? (
          <div className="bg-white border-2 border-[#4caf50] rounded-2xl p-8 md:p-10 text-center shadow-[0_20px_50px_rgba(0,0,0,0.06)]">
            <div className="mx-auto mb-5 w-14 h-14 rounded-full bg-[#e8f5e9] flex items-center justify-center">
              <Check className="w-8 h-8 text-[#2e7d32]" strokeWidth={3} />
            </div>
            <h2 className="text-2xl md:text-3xl font-black text-[#1a1a1a] mb-3">Thanks!</h2>
            <p className="text-[#1a1a1a]/75 mb-1">We've received your quote request.</p>
            <p className="text-[#1a1a1a]/75 mb-8">
              One of our team will contact you shortly with pricing and availability.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="tel:1800556654"
                data-call-source="quote_success"
                aria-label="Call 1800 556 654"
                className="inline-flex items-center justify-center gap-2 bg-[#1a1a1a] text-[#FFC300] px-6 py-4 rounded-xl font-bold hover:opacity-90 transition-opacity"
              >
                <Phone className="w-5 h-5" /> Call 1800 556 654
              </a>
              <a
                href="/"
                className="inline-flex items-center justify-center gap-2 bg-white border-2 border-[#1a1a1a]/15 text-[#1a1a1a] px-6 py-4 rounded-xl font-bold hover:border-[#1a1a1a]/40 transition-colors"
              >
                <Home className="w-5 h-5" /> Back to Home
              </a>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-[#1a1a1a]/8 shadow-[0_20px_50px_rgba(0,0,0,0.06)] p-6 md:p-10">
            {/* Progress */}
            <div
              className="flex justify-center mb-8"
              role="group"
              aria-label={`Step ${step} of ${TOTAL_STEPS}`}
            >
              <div className="flex items-center gap-2 sm:gap-2.5" aria-hidden="true">
                {Array.from({ length: TOTAL_STEPS }).map((_, i) => {
                  const n = i + 1;
                  const completed = n !== step && isStepComplete(n);
                  const current = n === step;
                  return (
                    <span
                      key={i}
                      className={`flex items-center justify-center rounded-full transition-all duration-300 ease-out motion-reduce:transition-none ${
                        current
                          ? "w-7 h-2.5 bg-[#FFC300]"
                          : completed
                            ? "w-5 h-5 bg-[#e8f5e9] ring-1 ring-[#4caf50]/45"
                            : "w-2.5 h-2.5 bg-[#1a1a1a]/15"
                      }`}
                    >
                      {completed && (
                        <Check
                          strokeWidth={3.5}
                          className="w-3 h-3 text-[#2e7d32] animate-in fade-in zoom-in-75 duration-300 motion-reduce:animate-none"
                        />
                      )}
                    </span>
                  );
                })}
              </div>
            </div>

            <form onSubmit={handleSubmit} noValidate>
              <div
                key={step}
                className={`animate-in fade-in duration-300 motion-reduce:slide-in-from-right-0 motion-reduce:slide-in-from-left-0 ${
                  dir === "fwd" ? "slide-in-from-right-4" : "slide-in-from-left-4"
                }`}
              >
                {step === 1 && (
                  <>
                    <h2 className="text-2xl md:text-3xl font-black text-[#1a1a1a] mb-2">
                      Let's get your quote started
                    </h2>
                    <p className="text-[#1a1a1a]/75 mb-7">Tell us where we can send your quote.</p>
                    <div className="space-y-4">
                      <FloatingField
                        id="quote-name"
                        label="Full Name"
                        inputRef={firstFieldRef}
                        autoComplete="name"
                        inputMode="text"
                        value={form.name}
                        onChange={(v) => setField("name", v)}
                        onBlur={() => markTouched("name")}
                        error={errors.name}
                        valid={fieldValid("name")}
                      />
                      <div>
                        <AuMobileInput
                          id="quote-phone"
                          label="Phone Number"
                          errorId="phone-error"
                          isValid={fieldValid("phone")}
                          value={form.phone}
                          onChange={(v) => setField("phone", v)}
                          onBlur={() => {
                            markTouched("phone");
                            if (form.phone.trim() && !isValidAuMobile(form.phone))
                              setErrors((prev) => ({ ...prev, phone: AU_MOBILE_ERROR }));
                          }}
                          hasError={Boolean(errors.phone)}
                          inputClassName="w-full px-4 bg-white text-[#1a1a1a] font-medium placeholder:text-[#1a1a1a]/40 focus:outline-none"
                          ariaLabel="Australian mobile number"
                        />
                        {errorText("phone")}
                      </div>
                      <FloatingField
                        id="quote-email"
                        label="Email Address"
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        value={form.email}
                        onChange={(v) => setField("email", v)}
                        onBlur={() => markTouched("email")}
                        error={errors.email}
                        valid={fieldValid("email")}
                      />
                    </div>
                  </>
                )}

                {step === 2 && (
                  <>
                    <h2 className="text-2xl md:text-3xl font-black text-[#1a1a1a] mb-7">
                      How big is your move?
                    </h2>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {MOVE_SIZES.map((option) => (
                        <SelectCard
                          key={option}
                          label={option}
                          selected={form.move_size === option}
                          onClick={() => setField("move_size", option)}
                        />
                      ))}
                    </div>
                    {errorText("move_size")}
                  </>
                )}

                {step === 3 && (
                  <>
                    <h2 className="text-2xl md:text-3xl font-black text-[#1a1a1a] mb-7">
                      When are you moving?
                    </h2>
                    <label className="block text-sm font-bold text-[#1a1a1a] mb-2">
                      Preferred Moving Date
                      <span className="sr-only"> (optional if your date is flexible)</span>
                    </label>
                    <input
                      type="date"
                      id="quote-move-date"
                      aria-label="Preferred moving date"
                      aria-invalid={errors.move_date ? true : undefined}
                      aria-describedby={errors.move_date ? "move_date-error" : undefined}
                      disabled={form.date_flexible}
                      value={form.move_date}
                      onChange={(e) => {
                        markTouched("move_date");
                        setField("move_date", e.target.value);
                      }}
                      className={`${inputClass("move_date")} disabled:opacity-50 disabled:cursor-not-allowed`}
                    />
                    {errorText("move_date")}
                    <div className="mt-4">
                      <SelectCard
                        label="My moving date is flexible"
                        selected={form.date_flexible}
                        onClick={() => {
                          const flexible = !form.date_flexible;
                          setField("date_flexible", flexible);
                          if (flexible) setField("move_date", "");
                          setErrors((prev) => ({ ...prev, move_date: undefined }));
                        }}
                      />
                    </div>
                  </>
                )}

                {step === 4 && (
                  <>
                    <h2 className="text-2xl md:text-3xl font-black text-[#1a1a1a] mb-7">
                      Where are you moving?
                    </h2>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <FloatingField
                        id="quote-pickup"
                        label="Pickup Suburb"
                        inputRef={firstFieldRef}
                        autoComplete="address-level2"
                        value={form.pickup_suburb}
                        onChange={(v) => setField("pickup_suburb", v)}
                        onBlur={() => markTouched("pickup_suburb")}
                        error={errors.pickup_suburb}
                        valid={fieldValid("pickup_suburb")}
                      />
                      <FloatingField
                        id="quote-dropoff"
                        label="Drop off Suburb"
                        autoComplete="address-level2"
                        value={form.dropoff_suburb}
                        onChange={(v) => setField("dropoff_suburb", v)}
                        onBlur={() => markTouched("dropoff_suburb")}
                        error={errors.dropoff_suburb}
                        valid={fieldValid("dropoff_suburb")}
                      />
                    </div>
                    <p className="text-sm font-bold text-[#1a1a1a] mt-7 mb-3">Access</p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {ACCESS_OPTIONS.map((option) => (
                        <SelectCard
                          key={option}
                          label={option}
                          selected={form.access === option}
                          onClick={() => setField("access", option)}
                        />
                      ))}
                    </div>
                    {errorText("access")}
                  </>
                )}
              </div>

              <div className="mt-8 space-y-3">
                <button type="submit" disabled={loading} className={primaryBtn}>
                  {step === 1 && "Next → Move Size"}
                  {step === 2 && "Next → Moving Date"}
                  {step === 3 && "Next → Locations"}
                  {step === 4 && (loading ? "Submitting…" : "Get My Free Quote")}
                </button>
                {step > 1 && (
                  <button
                    type="button"
                    onClick={back}
                    className={`w-full py-3 rounded-xl text-sm font-bold text-[#1a1a1a]/70 hover:text-[#1a1a1a] transition-colors ${FOCUS_RING}`}
                  >
                    ← Back
                  </button>
                )}
              </div>
            </form>

            <div className="mt-6 pt-6 border-t border-[#1a1a1a]/8 flex flex-wrap justify-center gap-x-5 gap-y-2">
              {TRUST.map((item) => (
                <span
                  key={item}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1a1a1a]/70"
                >
                  <Check className="w-3.5 h-3.5 text-[#2e7d32]" strokeWidth={3} />
                  {item}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default QuoteForm;
